// ========================================
// TELEMETRY SYSTEM
// Sistema de métricas y logging para debugging
// ========================================

/**
 * Métricas de la sesión actual
 */
const SESSION_METRICS = {
    // Contadores generales
    problemsGenerated: 0,
    sessionsStarted: 0,

    // Llamadas a APIs
    apiCalls: {
        gemini: 0,
        imagen: 0,
        total: 0
    },

    // Errores encontrados
    errors: [],

    // Fallbacks utilizados
    fallbacksUsed: {
        mathAgent: 0,
        narrativeAgent: 0,
        visualAgent: 0,
        total: 0
    },

    // Tiempos de generación (en ms)
    generationTimes: {
        math: [],
        narrative: [],
        visual: [],
        total: []
    },

    // Distribución de contenido
    distribution: {
        contexts: {},
        levels: {},
        problemTypes: {}
    },

    // Timestamp de inicio de sesión
    sessionStart: Date.now()
};

/**
 * Configuración de telemetría
 */
const TELEMETRY_CONFIG = {
    enabled: true,
    enableAnalytics: false, // Cambiar a true si se usa Google Analytics
    verboseLogging: false
};

/**
 * Registra una métrica
 * @param {string} category - Categoría de la métrica (ej: 'api', 'error', 'generation')
 * @param {string} event - Evento específico
 * @param {Object} metadata - Datos adicionales
 */
function logMetric(category, event, metadata = {}) {
    const metric = {
        timestamp: Date.now(),
        category,
        event,
        metadata
    };

    // Log a consola con formato
    const emoji = {
        'api': '🌐',
        'error': '❌',
        'generation': '🎨',
        'fallback': '⚠️',
        'success': '✅',
        'performance': '⏱️'
    }[category] || '📊';

    console.log(`${emoji} [METRIC] ${category}:${event}`, metadata);

    // Actualizar contadores según categoría
    updateMetricCounters(category, event, metadata);

    // Enviar a Google Analytics si está disponible
    if (typeof gtag !== 'undefined' && TELEMETRY_CONFIG.enableAnalytics) {
        gtag('event', event, {
            event_category: category,
            ...metadata
        });
    }

    // ED-07: Telemetría para Editorial (Mock)
    if (window.PUBLISHER_CONFIG && window.PUBLISHER_CONFIG.telemetry && window.PUBLISHER_CONFIG.telemetry.endpointUrl) {
        // En una app real usaríamos fetch()
        console.log(`[ED-07] 📡 Enviando métrica a editorial: ${window.PUBLISHER_CONFIG.telemetry.endpointUrl}`, metric);
    }

    return metric;
}

/**
 * Actualiza contadores internos basados en la métrica
 */
function updateMetricCounters(category, event, metadata) {
    switch (category) {
        case 'api':
            if (event === 'gemini_call') SESSION_METRICS.apiCalls.gemini++;
            if (event === 'imagen_call') SESSION_METRICS.apiCalls.imagen++;
            SESSION_METRICS.apiCalls.total++;
            break;

        case 'error':
            SESSION_METRICS.errors.push({
                event,
                ...metadata,
                timestamp: Date.now()
            });
            break;

        case 'fallback':
            if (metadata.agent) {
                SESSION_METRICS.fallbacksUsed[metadata.agent]++;
                SESSION_METRICS.fallbacksUsed.total++;
            }
            break;

        case 'generation':
            if (event === 'problems_generated') {
                SESSION_METRICS.problemsGenerated += (metadata.count || 0);
            }
            if (event === 'session_started') {
                SESSION_METRICS.sessionsStarted++;
            }
            break;

        case 'performance':
            if (metadata.agent && metadata.duration) {
                const times = SESSION_METRICS.generationTimes[metadata.agent];
                if (times) times.push(metadata.duration);
            }
            break;
    }

    // Actualizar distribución
    if (metadata.context) {
        SESSION_METRICS.distribution.contexts[metadata.context] =
            (SESSION_METRICS.distribution.contexts[metadata.context] || 0) + 1;
    }
    if (metadata.level) {
        SESSION_METRICS.distribution.levels[metadata.level] =
            (SESSION_METRICS.distribution.levels[metadata.level] || 0) + 1;
    }
    if (metadata.problemType) {
        SESSION_METRICS.distribution.problemTypes[metadata.problemType] =
            (SESSION_METRICS.distribution.problemTypes[metadata.problemType] || 0) + 1;
    }
}

/**
 * Inicia el seguimiento de un temporizador
 * @param {string} label - Etiqueta del timer
 * @returns {Function} Función para detener el timer
 */
function startTimer(label) {
    const startTime = performance.now();

    return function stopTimer() {
        const duration = performance.now() - startTime;
        console.log(`⏱️ [TIMER] ${label}: ${duration.toFixed(2)}ms`);
        return duration;
    };
}

/**
 * Calcula estadísticas de tiempos
 */
function calculateTimeStats(times) {
    if (times.length === 0) return { avg: 0, min: 0, max: 0 };

    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    const min = Math.min(...times);
    const max = Math.max(...times);

    return { avg: avg.toFixed(2), min: min.toFixed(2), max: max.toFixed(2) };
}

/**
 * Exporta métricas en formato legible
 * @returns {Object} Resumen de métricas
 */
function exportMetrics() {
    const sessionDuration = Date.now() - SESSION_METRICS.sessionStart;

    return {
        summary: {
            sessionDuration: `${(sessionDuration / 1000 / 60).toFixed(2)} minutos`,
            problemsGenerated: SESSION_METRICS.problemsGenerated,
            sessionsStarted: SESSION_METRICS.sessionsStarted,
            totalApiCalls: SESSION_METRICS.apiCalls.total,
            totalErrors: SESSION_METRICS.errors.length,
            totalFallbacks: SESSION_METRICS.fallbacksUsed.total
        },
        apiCalls: SESSION_METRICS.apiCalls,
        fallbacks: SESSION_METRICS.fallbacksUsed,
        performance: {
            math: calculateTimeStats(SESSION_METRICS.generationTimes.math),
            narrative: calculateTimeStats(SESSION_METRICS.generationTimes.narrative),
            visual: calculateTimeStats(SESSION_METRICS.generationTimes.visual),
            total: calculateTimeStats(SESSION_METRICS.generationTimes.total)
        },
        distribution: SESSION_METRICS.distribution,
        recentErrors: SESSION_METRICS.errors.slice(-5) // Últimos 5 errores
    };
}

/**
 * Imprime métricas en consola de forma legible
 */
function printMetrics() {
    const metrics = exportMetrics();

    console.log('\n📊 ═══════════════════════════════════════');
    console.log('📊 MÉTRICAS DE SESIÓN');
    console.log('📊 ═══════════════════════════════════════\n');

    console.log('⏱️  Duración:', metrics.summary.sessionDuration);
    console.log('🎲 Problemas generados:', metrics.summary.problemsGenerated);
    console.log('🔄 Sesiones iniciadas:', metrics.summary.sessionsStarted);
    console.log('🌐 Llamadas API:', metrics.summary.totalApiCalls,
        `(Gemini: ${metrics.apiCalls.gemini}, Imagen: ${metrics.apiCalls.imagen})`);
    console.log('⚠️  Fallbacks usados:', metrics.summary.totalFallbacks);
    console.log('❌ Errores:', metrics.summary.totalErrors);

    if (metrics.performance.total.avg > 0) {
        console.log('\n⏱️  RENDIMIENTO:');
        console.log('   Math Agent:', metrics.performance.math);
        console.log('   Narrative Agent:', metrics.performance.narrative);
        console.log('   Visual Agent:', metrics.performance.visual);
    }

    console.log('\n📊 ═══════════════════════════════════════\n');
}

/**
 * Resetea las métricas
 */
function resetMetrics() {
    SESSION_METRICS.problemsGenerated = 0;
    SESSION_METRICS.sessionsStarted = 0;
    SESSION_METRICS.apiCalls = { gemini: 0, imagen: 0, total: 0 };
    SESSION_METRICS.errors = [];
    SESSION_METRICS.fallbacksUsed = { mathAgent: 0, narrativeAgent: 0, visualAgent: 0, total: 0 };
    SESSION_METRICS.generationTimes = { math: [], narrative: [], visual: [], total: [] };
    SESSION_METRICS.distribution = { contexts: {}, levels: {}, problemTypes: {} };
    SESSION_METRICS.sessionStart = Date.now();

    console.log('🔄 Métricas reseteadas');
}



// Exportar para uso global
if (typeof window !== 'undefined') {
    window.SESSION_METRICS = SESSION_METRICS;
    window.logMetric = logMetric;
    window.startTimer = startTimer;
    window.exportMetrics = exportMetrics;
    window.printMetrics = printMetrics;
    window.resetMetrics = resetMetrics;
    window.TELEMETRY_CONFIG = TELEMETRY_CONFIG;

    // Añadir comando de consola para ver métricas
    window.showMetrics = printMetrics;
}

console.log('✅ [Telemetry] Sistema de telemetría cargado');
console.log('💡 Usa showMetrics() en consola para ver estadísticas');
